import { useFormatoNoBlur } from '@/hooks/use-formato-no-blur';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { definirFiltrosTela, limparFiltrosTela } from '@/components/copiloto/contexto-tela-store';
import { MAX_CENARIOS, type CenarioSimuladorResponse, type ConteudoCenarioDto, type CenariosDto, type PrecificarDto } from '@triade/contracts';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { useCenarioSimulador } from '@/api/cenarios-simulador';
import { useItem, useItens } from '@/api/itens';
import { usePode } from '@/api/me';
import { useCenarios, useGravarPrecificacao } from '@/api/precificacao';
import { useTabelasPreco } from '@/api/tabelas-preco';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { PainelReverso } from '@/components/formacao/painel-reverso';
import { ItemCombobox, type ItemResumo } from '@/components/item-combobox';
import { RecordToolbar } from '@/components/record-toolbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { decimalDigitado, decimalParaTextoCampo, formatarCusto, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { CartoesCenarios, Comparacao, type CenarioNaTela } from './comparacao';
import { Premissas, type ValoresBase } from './premissas';
import { BotaoRelatorios } from '@/relatorios';
import { CenariosSalvos, mensagemCenario } from './cenarios-salvos';
import { SalvarCenariosDialog, type CenarioAberto } from './salvar-cenarios-dialog';
import type { BuscaSimulador } from './busca';
import { Vazio } from './vazio';
import { CAMPOS, cenarioVazio, lerCenario, paraAjustes, temAjustes, type Campo, type CenarioForm } from './tipos';

function hoje(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const TRIGGER = 'h-10 w-full rounded-[10px] bg-card';

/** Cenario salvo (texto como o usuario digitou) -> formulario. Campos ausentes viram vazio. */
function cenarioDoConteudo(c: ConteudoCenarioDto['cenarios'][number]): CenarioForm {
  const campos = Object.fromEntries(CAMPOS.map((k) => [k, c.campos[k] ?? ''])) as Record<Campo, string>;
  return { id: c.id, nome: c.nome, campos };
}

/** Troca a busca da rota (abrir, novo, voltar para a lista). */
type Navegar = (busca: BuscaSimulador, opcoes?: { replace?: boolean }) => void;

export interface SimuladorPageProps {
  /** Busca da rota: sem cenario/novo mostra a lista dos cenarios salvos. */
  busca?: BuscaSimulador;
  onNavegar?: Navegar;
}

/** Tela do simulador: lista dos cenarios salvos, um cenario aberto (`?cenario=`) ou simulador em branco (`?novo=1`). */
export function SimuladorPage({ busca = {}, onNavegar = () => undefined }: SimuladorPageProps) {
  if (busca.cenario) return <AbrirCenario key={busca.cenario} id={busca.cenario} onNavegar={onNavegar} />;
  if (busca.novo) return <SimuladorForm inicial={null} onNavegar={onNavegar} />;
  return <CenariosSalvos onAbrir={(id) => onNavegar({ cenario: id })} onNovo={() => onNavegar({ novo: 1 })} />;
}

/** Carrega o cenario salvo e o item dele; so monta o formulario quando os dois chegaram. */
function AbrirCenario({ id, onNavegar }: { id: string; onNavegar: Navegar }) {
  const carga = useCenarioSimulador(id);
  const item = useItem(carga.data?.conteudo.itemId);
  if (carga.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-5">
        <p className="text-danger-ink">{mensagemCenario(carga.error)}</p>
        <Button type="button" variant="outline" onClick={() => onNavegar({})}>Voltar aos cenários salvos</Button>
      </div>
    );
  }
  if (item.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-5">
        <p className="text-danger-ink">{mensagemCenario(item.error)}</p>
        <Button type="button" variant="outline" onClick={() => onNavegar({})}>Voltar aos cenários salvos</Button>
      </div>
    );
  }
  if (!carga.data || !item.data) return <Vazio papel="status">Carregando cenário…</Vazio>;
  return <SimuladorForm key={carga.data.id} inicial={{ cenario: carga.data, item: item.data }} onNavegar={onNavegar} />;
}

interface FormularioInicial {
  cenario: CenarioSimuladorResponse;
  item: ItemResumo;
}

function SimuladorForm({ inicial, onNavegar }: { inicial: FormularioInicial | null; onNavegar: Navegar }) {
  const { empresaId } = useSessao();
  const { pode } = usePode();
  const conteudo = inicial?.cenario.conteudo ?? null;
  const proximoId = useRef(conteudo ? Math.max(1, ...conteudo.cenarios.map((c) => c.id)) + 1 : 2);
  const [item, setItem] = useState<ItemResumo | null>(inicial?.item ?? null);
  const [tabelaId, setTabelaId] = useState(conteudo?.tabelaId ?? '');
  const [condicaoId, setCondicaoId] = useState(conteudo?.condicaoId ?? '');
  const [data, setData] = useState(() => conteudo?.data ?? hoje());
  const [praticado, setPraticado] = useState(conteudo?.praticado ?? '');
  const praticadoFormatado = useFormatoNoBlur(praticado, 'dinheiro');
  // Selecao para o copiloto (fora da URL): so ids e o praticado digitado; limpa ao sair da tela.
  useEffect(() => {
    definirFiltrosTela('simulador', { itemId: item?.id, tabelaId, condicaoId, praticado });
  }, [item?.id, tabelaId, condicaoId, praticado]);
  useEffect(() => () => limparFiltrosTela('simulador'), []);
  const [cenarios, setCenarios] = useState<CenarioForm[]>(() => (conteudo ? conteudo.cenarios.map(cenarioDoConteudo) : [cenarioVazio(1)]));
  const [selId, setSelId] = useState(conteudo?.cenarios[0]?.id ?? 1);
  const [termo, setTermo] = useState('');
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [salvoAssinatura, setSalvoAssinatura] = useState<string | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  // Cenario salvo aberto (regravado por "Salvar") e dialogo "Salvar cenários…".
  const [aberto, setAberto] = useState<CenarioAberto | null>(inicial ? { id: inicial.cenario.id, nome: inicial.cenario.nome, anotacoes: inicial.cenario.anotacoes } : null);
  const [dialogoSalvar, setDialogoSalvar] = useState(false);
  const termoBusca = useDebounced(termo.trim(), 250);

  const itens = useItens({ q: termoBusca || undefined, ativo: true, comerciavel: true, limit: 20 });
  const tabelas = useTabelasPreco({ ativa: true, limit: 200 });
  const condicoes = useCondicoesPagamento({ ativa: true, limit: 200 });
  const gravar = useGravarPrecificacao();

  const sel = cenarios.find((c) => c.id === selId) ?? cenarios[0]!;
  const praticadoValido = decimalDigitado(praticado);
  const praticadoInvalido = praticado.trim() !== '' && praticadoValido === null;

  // Ajustes por cenario (texto -> contrato). Cenarios com campo invalido nao vao para a API ate corrigir.
  const convertidos = useMemo(() => cenarios.map((c) => ({ c, ...paraAjustes(c) })), [cenarios]);
  const temInvalido = convertidos.some((x) => x.invalidos.length > 0) || praticadoInvalido;

  const dto = useMemo<CenariosDto | null>(() => {
    if (!item || !tabelaId || !condicaoId || !data || temInvalido) return null;
    return {
      itemId: item.id,
      tabelaId,
      condicaoId,
      data,
      ...(praticadoValido !== null ? { praticado: praticadoValido } : {}),
      cenarios: convertidos.map(({ c, ajustes }) => ({ ...(c.nome.trim() ? { nome: c.nome.trim() } : { nome: `Cenário ${c.id}` }), ajustes })),
    };
  }, [item, tabelaId, condicaoId, data, praticadoValido, convertidos, temInvalido]);
  const ids = useMemo(() => cenarios.map((c) => c.id), [cenarios]);
  const envio = useMemo(() => (dto ? { dto, ids } : null), [dto, ids]);
  const envioDebounced = useDebounced(envio, 400);
  const consulta = useCenarios(envioDebounced?.dto ?? null, envioDebounced?.ids ?? []);

  const lido = useMemo(() => {
    if (!consulta.data) return null;
    const base: CenarioNaTela = { ...lerCenario(consulta.data.base), chave: 0 };
    const lista: CenarioNaTela[] = consulta.data.cenarios.map((c, i) => ({ ...lerCenario(c), chave: consulta.data.ids[i] ?? i + 1 }));
    return { base, lista, snapshot: consulta.data.snapshot as { company?: { financialCostRate?: string } } };
  }, [consulta.data]);

  // Resposta defasada em relacao ao formulario (debounce ou refetch): so para sinalizar "calculando".
  const atualizando = dto !== null && (JSON.stringify(envio) !== JSON.stringify(envioDebounced) || consulta.isFetching);
  const base = lido?.base ?? null;
  const valoresBase: ValoresBase | null =
    base?.resultado && base.composicao
      ? {
          lucroDesejado: base.resultado.desiredProfitRate,
          despesaVariavel: base.resultado.variableExpenseRate,
          despesaFixa: base.resultado.fixedExpenseRate,
          taxaFinanceira: lido?.snapshot.company?.financialCostRate ?? '',
          custo: base.composicao.sugerido.custoUnitario,
        }
      : null;

  const setCampo = useCallback((campo: Campo, valor: string) => {
    setCenarios((lista) =>
      lista.map((c) => {
        if (c.id !== selId) return c;
        const campos = { ...c.campos, [campo]: valor };
        // custo absoluto e variacao do custo sao excludentes
        if (campo === 'custo' && valor.trim() !== '') campos.custoVariacao = '';
        if (campo === 'custoVariacao' && valor.trim() !== '') campos.custo = '';
        return { ...c, campos };
      }),
    );
  }, [selId]);

  function adicionar() {
    if (cenarios.length >= MAX_CENARIOS) return;
    const id = proximoId.current++;
    setCenarios((l) => [...l, cenarioVazio(id)]);
    setSelId(id);
  }

  function remover(id: number) {
    if (cenarios.length <= 1) return;
    const restantes = cenarios.filter((c) => c.id !== id);
    setCenarios(restantes);
    if (selId === id) setSelId(restantes[0]!.id);
  }

  // Mesa atual no formato do contrato (texto como digitado). Nada e calculado aqui: so ids, datas e textos.
  const conteudoAtual: ConteudoCenarioDto | null =
    item && tabelaId && condicaoId && data
      ? { itemId: item.id, tabelaId, condicaoId, data, praticado, cenarios: cenarios.map((c) => ({ id: c.id, nome: c.nome, campos: c.campos })) }
      : null;

  function aoSalvarCenario(salvo: CenarioSimuladorResponse) {
    setDialogoSalvar(false);
    if (aberto && salvo.id === aberto.id) {
      setAberto({ id: salvo.id, nome: salvo.nome, anotacoes: salvo.anotacoes });
      return;
    }
    // Cenario novo: a URL passa a apontar para ele (recarrega do servidor, como ao abrir).
    onNavegar({ cenario: salvo.id }, { replace: true });
  }

  const selConv = convertidos.find((x) => x.c.id === sel.id);
  const assinatura = JSON.stringify([item?.id, tabelaId, condicaoId, data, praticadoValido, selConv?.ajustes]);
  const dirty = temAjustes(sel) && item !== null && tabelaId !== '' && condicaoId !== '' && assinatura !== salvoAssinatura;
  const pronto = dirty && consulta.isSuccess && !atualizando && !temInvalido && !(lido?.lista.find((c) => c.chave === sel.id)?.erro);

  async function salvar() {
    if (!pronto || !item || !selConv) return;
    setErroSalvar(null);
    const corpo: PrecificarDto = {
      itemId: item.id,
      tabelaId,
      condicaoId,
      data,
      ...(praticadoValido !== null ? { praticado: praticadoValido } : {}),
      ajustes: selConv.ajustes,
    };
    try {
      await gravar.mutateAsync(corpo);
      setSalvoAssinatura(assinatura);
      setSalvoEm(new Date());
    } catch (e) {
      setErroSalvar(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
    }
  }

  const cadastrosErro = tabelas.error ?? condicoes.error;
  const cadastrosCarregando = tabelas.isPending || condicoes.isPending;
  const semEmpresa = empresaId === null;
  const nomeTabela = tabelas.data?.find((t) => t.id === tabelaId)?.nome ?? '—';

  const selecionado = !semEmpresa && !cadastrosErro && item !== null && tabelaId !== '' && condicaoId !== '';
  const premissas = (
    <Premissas
      cenario={sel}
      base={valoresBase}
      invalidos={selConv?.invalidos ?? []}
      onCampo={setCampo}
      onNome={(nome) => setCenarios((l) => l.map((c) => (c.id === sel.id ? { ...c, nome } : c)))}
      onLimpar={() => setCenarios((l) => l.map((c) => (c.id === sel.id ? { ...cenarioVazio(c.id), nome: c.nome } : c)))}
    />
  );

  let resultado: React.ReactNode;
  if (semEmpresa) {
    resultado = <Vazio>Escolha a empresa no menu lateral para operar.</Vazio>;
  } else if (cadastrosErro) {
    resultado = <ErroFormacao erro={cadastrosErro} onTentar={() => { void tabelas.refetch(); void condicoes.refetch(); }} />;
  } else if (!selecionado) {
    resultado = <Vazio>Escolha o produto ou serviço, a tabela de preço e a condição de pagamento para simular cenários.</Vazio>;
  } else {
    let direita: React.ReactNode;
    if (temInvalido) direita = <Vazio>Corrija os campos com valor inválido para recalcular.</Vazio>;
    else if (consulta.isError) direita = <ErroFormacao erro={consulta.error} onTentar={() => void consulta.refetch()} />;
    else if (!lido) direita = <Vazio papel="status">Calculando…</Vazio>;
    else direita = <Comparacao base={lido.base} cenarios={lido.lista} selecionado={sel.id} onSelecionar={setSelId} carregando={atualizando} />;
    resultado = (
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-start gap-5">
          {premissas}
          <div className="min-w-0 flex-[999_1_560px]">{direita}</div>
        </div>
        {lido && !temInvalido && !consulta.isError && <CartoesCenarios itens={[lido.base, ...lido.lista]} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Precificar <span aria-hidden="true">/</span> Simulador
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Simulador de cenários</h1>
          <p className="mt-1 text-ink-secondary">Mude uma premissa e veja o efeito no preço antes de mexer no cadastro.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onNavegar({})}
            className="inline-flex h-10 items-center gap-1.5 rounded-[10px] px-2 text-small font-medium text-primary hover:underline"
          >
            <ArrowLeft aria-hidden="true" className="size-4" /> Cenários salvos
          </button>
          <button
            type="button"
            onClick={() => setDialogoSalvar(true)}
            disabled={!conteudoAtual || temInvalido || semEmpresa}
            className="h-10 rounded-[10px] border border-border bg-card px-3.5 font-medium hover:bg-line-soft disabled:pointer-events-none disabled:opacity-50"
          >
            Salvar cenários…
          </button>
          <BotaoRelatorios tela="simulador" params={{ item: item?.codigo }} dados={consulta.isPlaceholderData ? undefined : consulta.data} />
          <button
            type="button"
            onClick={() => void salvar()}
            disabled={!pronto || gravar.isPending}
            className="h-10 rounded-[10px] border border-border bg-card px-3.5 font-medium hover:bg-line-soft disabled:pointer-events-none disabled:opacity-50"
          >
            Salvar cenário como formação
          </button>
        </div>
      </div>

      <section aria-label="Base" className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3.5 rounded-2xl border border-border bg-card p-4">
        <div className="min-w-0 sm:col-span-2">
          <Label htmlFor="s-produto" className={ROTULO}>Produto ou serviço</Label>
          <ItemCombobox
            id="s-produto"
            valor={item}
            onSelecionar={setItem}
            itens={itens.data ?? []}
            carregando={itens.isFetching}
            erro={itens.error?.message ?? null}
            termo={termo}
            onTermoChange={setTermo}
            disabled={semEmpresa}
          />
        </div>
        <div>
          <Label htmlFor="s-tabela" className={ROTULO}>Tabela de preço</Label>
          <Select value={tabelaId} onValueChange={setTabelaId} disabled={semEmpresa || cadastrosCarregando}>
            <SelectTrigger id="s-tabela" className={TRIGGER}>
              <SelectValue placeholder={tabelas.isPending ? 'Carregando…' : 'Escolha a tabela'} />
            </SelectTrigger>
            <SelectContent>
              {(tabelas.data ?? []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {tabelas.data?.length === 0 && <p className="mt-1 text-caption text-ink-muted">Nenhuma tabela de preço ativa.</p>}
        </div>
        <div>
          <Label htmlFor="s-cond" className={ROTULO}>Condição de pagamento</Label>
          <Select value={condicaoId} onValueChange={setCondicaoId} disabled={semEmpresa || cadastrosCarregando}>
            <SelectTrigger id="s-cond" className={TRIGGER}>
              <SelectValue placeholder={condicoes.isPending ? 'Carregando…' : 'Escolha a condição'} />
            </SelectTrigger>
            <SelectContent>
              {(condicoes.data ?? []).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {condicoes.data?.length === 0 && <p className="mt-1 text-caption text-ink-muted">Nenhuma condição de pagamento ativa.</p>}
        </div>
        <div>
          <Label htmlFor="s-data" className={ROTULO}>Data da operação</Label>
          <Input id="s-data" type="date" value={data} onChange={(e) => setData(e.target.value)} disabled={semEmpresa} />
        </div>
        <div>
          <Label htmlFor="s-pratic" className={ROTULO}>Preço praticado (todos os cenários)</Label>
          <div className="flex h-10 items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 focus-within:border-primary focus-within:shadow-focus">
            <span aria-hidden="true" className="text-ink-muted">R$</span>
            <input
              id="s-pratic"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0,00"
              {...praticadoFormatado}
              onChange={(e) => setPraticado(e.target.value)}
              aria-invalid={praticadoInvalido || undefined}
              disabled={semEmpresa}
              className="tabular w-full border-0 bg-transparent font-semibold outline-none"
            />
          </div>
          {praticadoInvalido && <p role="alert" className="mt-1 text-caption text-danger-ink">Informe um valor como 25,00.</p>}
        </div>
        {base?.composicao && base.resultado && (
          <dl className="col-span-full flex flex-wrap gap-x-8 gap-y-2 border-t border-dashed border-border pt-3" data-testid="resumo-base">
            <div><dt className="text-caption text-ink-muted">Custo</dt><dd className="tabular font-semibold">{formatarCusto(base.composicao.sugerido.custoUnitario)}</dd></div>
            <div><dt className="text-caption text-ink-muted">Tabela</dt><dd className="font-semibold">{nomeTabela}</dd></div>
            <div><dt className="text-caption text-ink-muted">Lucro desejado</dt><dd className="tabular font-semibold">{formatarFracaoPercentual(base.resultado.desiredProfitRate)}</dd></div>
            <div><dt className="text-caption text-ink-muted">Sugerido hoje</dt><dd className="tabular font-semibold">{formatarMoeda(base.composicao.sugerido.preco)}</dd></div>
          </dl>
        )}
      </section>

      <div role="tablist" aria-label="Cenários" className="flex flex-wrap items-center gap-2">
        {cenarios.map((c) => (
          <div key={c.id} className={cn('flex items-center rounded-full border text-small', c.id === sel.id ? 'border-primary bg-primary/5 font-semibold' : 'border-border bg-card')}>
            <button type="button" role="tab" aria-selected={c.id === sel.id} onClick={() => setSelId(c.id)} className="rounded-full py-1.5 pr-2 pl-3.5">
              {c.nome.trim() || `Cenário ${c.id}`}
            </button>
            {cenarios.length > 1 && (
              <button type="button" aria-label={`Remover ${c.nome.trim() || `Cenário ${c.id}`}`} onClick={() => remover(c.id)} className="mr-1.5 rounded-full p-1 text-ink-muted hover:bg-line-soft">
                <X aria-hidden="true" className="size-3.5" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={adicionar}
          disabled={cenarios.length >= MAX_CENARIOS}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1.5 text-small hover:bg-line-soft disabled:pointer-events-none disabled:opacity-50"
        >
          <Plus aria-hidden="true" className="size-3.5" /> Cenário
        </button>
        <span className="text-caption text-ink-muted">até {MAX_CENARIOS} cenários, além da base</span>
      </div>

      {resultado}

      {item !== null && selConv && !temInvalido && tabelaId !== '' && condicaoId !== '' && pode('precificacao.simular') && pode('custo.ver') && (
        <PainelReverso
          referencia={{ itemId: item.id, tabelaId, condicaoId, data, ajustes: selConv.ajustes }}
          podeUsar
          onUsarComoPraticado={(preco) => setCampo('praticado', decimalParaTextoCampo(preco))}
        />
      )}

      {dialogoSalvar && conteudoAtual && (
        <SalvarCenariosDialog conteudo={conteudoAtual} aberto={aberto} onSalvo={aoSalvarCenario} onFechar={() => setDialogoSalvar(false)} />
      )}

      <RecordToolbar
        dirty={dirty}
        saving={gravar.isPending}
        savedAt={salvoEm}
        erro={erroSalvar}
        novo
        canPrev={false}
        canNext={false}
        alteradosCount={dirty ? Object.values(sel.campos).filter((v) => v.trim() !== '').length : undefined}
        onSave={() => void salvar()}
        onUndo={() => setCenarios((l) => l.map((c) => (c.id === sel.id ? { ...cenarioVazio(c.id), nome: c.nome } : c)))}
        onDelete={() => setErroSalvar('formações gravadas são imutáveis e não podem ser excluídas')}
      />
    </div>
  );
}
